import { z } from 'zod';
import { PersonNameSchema } from './primitives.js';

/*
 * Control and format characters (zero-width space, bidi marks and isolates),
 * the line and paragraph separators, the braille blank, the Hangul fillers,
 * the combining grapheme joiner, variation selectors (including the
 * supplement), the object replacement character, the Khmer inherent vowels and
 * lone surrogates. Checked on the raw input AND after NFKC, because U+3164 and
 * U+FFA0 normalise to U+1160, which is a letter.
 */
const INVISIBLE_SOURCE =
  '(?:(?![\\u{200C}\\u{200D}])[\\p{Cc}\\p{Cf}\\p{Cs}\\u{2028}\\u{2029}\\u{2800}\\u{3164}\\u{FFA0}\\u{115F}\\u{1160}\\u{FFFC}]|\\u{034F}|\\u{17B4}|\\u{17B5}|[\\u{FE00}-\\u{FE0F}]|[\\u{E0100}-\\u{E01EF}])';
const INVISIBLE = new RegExp(INVISIBLE_SOURCE, 'u');
const INVISIBLE_ALL = new RegExp(INVISIBLE_SOURCE, 'gu');

/** More than six stacked combining marks is a smear, not a letter. */
const MARK_STACK = /\p{M}{7,}/u;
const MARK_STACK_ALL = /(\p{M}{6})\p{M}+/gu;

/*
 * U+200C and U+200D are real letters-joiners in Sinhala, Persian and others,
 * so they are allowed, but only directly between two letters or marks.
 */
const MISPLACED_JOINER_SOURCE = String.raw`(?<![\p{L}\p{M}])[\u200C\u200D]|[\u200C\u200D](?![\p{L}\p{M}])`;
const MISPLACED_JOINER = new RegExp(MISPLACED_JOINER_SOURCE, 'u');
const MISPLACED_JOINER_ALL = new RegExp(MISPLACED_JOINER_SOURCE, 'gu');

const hasHidden = (value: string): boolean =>
  INVISIBLE.test(value) || MISPLACED_JOINER.test(value) || MARK_STACK.test(value);

/**
 * An account holder's name when edited: NFKC, trimmed, inner whitespace
 * collapsed to one space, no invisible characters, no stack of more than six
 * combining marks, at least one letter or number, 1 to 80 characters after all
 * of that.
 */
export const AccountNameSchema = z
  .string()
  .refine((value) => !hasHidden(value) && !hasHidden(value.normalize('NFKC')), {
    message: 'Invisible characters are not allowed',
  })
  .transform((value) => value.normalize('NFKC').trim().replace(/\s+/g, ' '))
  .refine((value) => /[\p{L}\p{N}]/u.test(value), { message: 'Needs a letter or a number' })
  .pipe(PersonNameSchema);

/**
 * For a name typed by somebody who has no account yet: the same characters
 * removed instead of refused, so a pasted name still goes through as plain
 * text. The result can be empty, and the caller falls back to something else.
 */
export const CleanedNameSchema = PersonNameSchema.transform((value) => {
  const cleaned = value
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .replace(INVISIBLE_ALL, '')
    .replace(MISPLACED_JOINER_ALL, '')
    .replace(MARK_STACK_ALL, '$1')
    .trim();
  return /[\p{L}\p{N}]/u.test(cleaned) ? cleaned : '';
});

export const NameUpdateSchema = z.object({ name: AccountNameSchema });
